import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import DataGrid from 'app/components/DataGrid';
import useResource from 'app/hooks/useResource';
import AccountBillingModal from './AccountBillingModal';

import {
  getAccountBillings,
  setAccountBillingParams
} from 'app/actions/billings';

import {
  getBillings,
  createAccountBilling,
  updateAccountBilling,
  destroyAccountBilling,
  getAccountBilling as exportAccountBillingSvc
} from 'app/services/billings';

import {
  accountBillings as resource
} from 'app/resources';

import {
  getAccounts
} from 'app/services/accounts';

import {
  formatPascalCase
} from 'app/utils/valueFormatters';

import {
  formatOptions
} from 'app/utils/options';

const AccountBilling = (props) => {
  const { getAccountBillings, setAccountBillingParams } = props;

  const getAccountOptions = async (page = 1, perPage = 100, filter = null) => {
    let payload = {
      "page": page,
      "per_page": perPage,
      "sort_model": []
    }

    if (filter) {
      payload.filter_model = {
        "name": [
          { "filterClass": "text", "filterType": "contains", "value": filter }
        ]
      }
    }

    let resp = await getAccounts(payload);

    return formatOptions(resp.records)
  }

  const getBillingOptions = async (page = 1, perPage = 100, filter = null) => {
    let payload = {
      "page": page,
      "per_page": perPage,
      "sort_model": []
    }

    if (filter) {
      payload.filter_model = {
        "name": [
          { "filterClass": "text", "filterType": "contains", "value": filter }
        ]
      }
    }

    let resp = await getBillings(payload);

    return formatOptions(resp.records);
  }

  const getAllAccounts = (page = 1, per_page = 100, accounts = []) => {
    return getAccounts({ page: page, per_page: per_page })
      .then(resp => {
        accounts = accounts.concat(resp.records);

        if (resp.records.length < per_page) {
          return Promise.resolve(accounts);
        } else {
          return getAllAccounts(page + 1, per_page, accounts);
        }
      });
  }

  const getAllBillings = (page = 1, per_page = 100, billings = []) => {
    return getBillings({ page: page, per_page: per_page })
      .then(resp => {
        billings = billings.concat(resp.records);

        if (resp.records.length < per_page) {
          return Promise.resolve(billings);
        } else {
          return getAllBillings(page + 1, per_page, billings);
        }bi
      });
  }

  // load needed resources on first render
  const [accounts, setAccounts] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [billings, setBillings] = useState([]);
  const [billingOptions, setBillingOptions] = useState([]);

  const header = 'Account Billings';
  const getRowData = getAccountBillings;
  const addButtonLabel = 'Add Account Billing';
  const columnOrder = ['billing_rec_name', 'account_name', 'billing_name'];
  const visibleColumns = ['billing_rec_name', 'account_name', 'billing_name'];

  const {
    onAdd,
    onEdit,
    formIsOpen,
    currentRecord,
    onDataGridLoad,
    onFormSubmit,
    onFormClose,
    onDestroy,
    showFilters,
    getFilterModel
  } = useResource({
    resource,
    getResource: getAccountBillings,
    createResource: createAccountBilling,
    updateResource: updateAccountBilling,
    destroyResource: destroyAccountBilling,
    setResourceParams: setAccountBillingParams,
  });

  useEffect(() => {
    getAllBillings().then(billings => {
      setBillings(billings);
      setBillingOptions(formatOptions(billings));
    });

    getAllAccounts().then(accounts => {
      setAccounts(accounts);
      setAccountOptions(formatOptions(accounts));
      // TODO 2: figure out if this is needed, it looks like I broke it
      // TODO: Not really ideal since this makes a 2nd call to account billing API
      // but ag-grid's refreshCells wasn't causing the cells to re-render once the accounts had been loaded
      //refreshGrid();
    });
  }, []);

  const exportCsv = async (row) => {
    let params = {
      page: 0,
      per_page: 1000,
      is_export: true
    }

    const filterModel = getFilterModel();
    if (filterModel) {
      params.filter_model = filterModel;
    }

    exportAccountBillingSvc(params).then((resp => resp.blob())).then((blob) => {
      const elem = window.document.createElement('a');
      elem.href = window.URL.createObjectURL(blob);
      elem.download = `account_billing_${new Date().toISOString()}.csv`;
      document.body.appendChild(elem);
      elem.click();
      document.body.removeChild(elem);
    })
  }

  const columnDefOverrides = visibleColumns.map((col) => {
    if (col === 'account_id') {
      return {
        field: 'account_id',
        headerName: 'Account Name',
        dataValueFormatter: (data, value) => {
          return accountOptions.find((i) => i.value === data.account_id)?.label;
        },
        quickEdit: true,
        inputType: 'autocomplete',
        getInputOptions: getAccountOptions,
        selectedOptionFormatter: data => formatAccountOptions([data.account_id])[0]
      }
    } else if (col === 'billing_id') {
      return {
        field: 'billing_id',
        headerName: 'Billing Name',
        dataValueFormatter: (data, value) => {
          return billingOptions.find((i) => i.value === data.billing_id)?.label;
        },
        quickEdit: true,
        inputType: 'autocomplete',
        getInputOptions: getBillingOptions,
        selectedOptionFormatter: data => formatBillingOptions([data.billing_id])[0]
      }
    } else if (col === 'billing_name') {
      return {
        field: 'billing_name',
        headerName: 'Customer ID',
      }
    } else if (col === 'billing_rec_name') {
      return {
        field: 'billing_rec_name',
        headerName: 'Billing Name',
      }
    } else {
      return {
        field: col,
        headerName: formatPascalCase(col)
      }
    }
  })

  const destroyAction = (row) => {
    return destroyAccountBilling(row.id)
  }

  const actions = [
    { key: 'destroy', label: 'Delete Account Billing Records', callback: destroyAction }
  ]

  const buttons = [
    {
      key: 'export',
      icon: <FileDownloadIcon />,
      label: 'CSV',
      onClick: exportCsv
    }
  ]


  return <div>
    <DataGrid
      resource={resource}
      header={header}
      getRowData={getRowData}
      addButtonLabel={addButtonLabel}
      onAdd={onAdd}
      showEdit={true}
      onEdit={onEdit}
      showQuickEdit={true}
      showSelect={true}
      loadingMessage="Loading account billing records..."
      columnOrder={columnOrder}
      visibleColumns={visibleColumns}
      columnDefOverrides={columnDefOverrides}
      showFilters={showFilters}
      onDataGridLoad={onDataGridLoad}
      actions={actions}
      buttons={buttons}
    />
    <AccountBillingModal
      isOpen={formIsOpen}
      onClose={onFormClose}
      onFormSubmit={onFormSubmit}
      onDestroy={onDestroy}
      accountBilling={currentRecord}
    />
  </div>
}
const mapStateToProps = state => ({
  user: state.userReducer.user,
});

export default connect(mapStateToProps, { getAccountBillings, setAccountBillingParams })(withRouter(AccountBilling));
